import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, DollarSign } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import type { RevenueDataPoint } from '@/hooks/useDashboardAnalytics';

interface RevenueAreaChartProps {
  data: RevenueDataPoint[];
  baseCurrency: string;
  className?: string;
}

export function RevenueAreaChart({ data, baseCurrency, className }: RevenueAreaChartProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: baseCurrency,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const totalRevenue = data.reduce((sum, item) => sum + item.amount, 0);
  const totalCount = data.reduce((sum, item) => sum + item.count, 0);

  const chartData = data.map((item) => ({
    date: item.date,
    amount: item.amount,
    count: item.count,
    formattedDate: format(parseISO(item.date), 'd MMM', { locale: ru }),
  }));

  const CustomTooltip = ({ active, payload, label }: {
    active?: boolean;
    payload?: Array<{ value: number; dataKey: string }>;
    label?: string;
  }) => {
    if (!active || !payload || payload.length === 0) {return null;}

    const item = chartData.find((d) => d.date === label);
    if (!item) {return null;}

    return (
      <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium mb-1">
          {format(parseISO(label ?? ''), 'd MMMM yyyy', { locale: ru })}
        </p>
        <p className="text-green-600">
          Выручка: {formatCurrency(payload[0]?.value ?? 0)}
        </p>
        <p className="text-muted-foreground">
          Платежей: {item.count}
        </p>
      </div>
    );
  };

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Выручка
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Подтвержденные платежи
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(totalRevenue)}
          </p>
          <p className="text-xs text-muted-foreground flex items-center justify-end gap-1">
            <TrendingUp className="h-3 w-3" />
            {totalCount} платежей
          </p>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="formattedDate"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12 }}
              tickMargin={8}
              interval="preserveStartEnd"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12 }}
              tickFormatter={(value) =>
                new Intl.NumberFormat('ru-RU', {
                  notation: 'compact',
                  compactDisplay: 'short',
                }).format(value)
              }
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="amount"
              stroke="#22c55e"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorRevenue)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
